import { useState } from "react";
import {
	Users,
	Star,
	Mail,
	Clock,
	TrendingUp,
	Filter,
	Search,
	ChevronRight,
	Flame,
	Snowflake,
	CheckCircle2,
	XCircle,
	UserPlus,
	BarChart3,
	Target,
	Zap,
	MessageSquare,
} from "lucide-react";
import type { Lead } from "@/lib/flow-types";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";

interface LeadsPanelProps {
	leads: Lead[];
}

const STATUS_CONFIG = {
	new: {
		label: "New",
		icon: UserPlus,
		color: "text-blue-400",
		bg: "bg-blue-500/10",
		border: "border-blue-500/20",
	},
	qualified: {
		label: "Qualified",
		icon: Star,
		color: "text-amber-400",
		bg: "bg-amber-500/10",
		border: "border-amber-500/20",
	},
	contacted: {
		label: "Contacted",
		icon: Mail,
		color: "text-purple-400",
		bg: "bg-purple-500/10",
		border: "border-purple-500/20",
	},
	converted: {
		label: "Converted",
		icon: CheckCircle2,
		color: "text-emerald-400",
		bg: "bg-emerald-500/10",
		border: "border-emerald-500/20",
	},
	lost: {
		label: "Lost",
		icon: XCircle,
		color: "text-zinc-500",
		bg: "bg-zinc-500/10",
		border: "border-zinc-500/20",
	},
};

function getScoreColor(score: number) {
	if (score >= 80) return "text-emerald-400";
	if (score >= 60) return "text-amber-400";
	if (score >= 40) return "text-orange-400";
	return "text-red-400";
}

function getScoreBg(score: number) {
	if (score >= 80)
		return "bg-gradient-to-r from-emerald-500 to-green-500";
	if (score >= 60)
		return "bg-gradient-to-r from-amber-500 to-yellow-500";
	if (score >= 40)
		return "bg-gradient-to-r from-orange-500 to-amber-500";
	return "bg-gradient-to-r from-red-500 to-orange-500";
}

function getScoreIcon(score: number) {
	if (score >= 80) return Flame;
	return Snowflake;
}

function timeAgo(date: Date) {
	const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
	if (seconds < 60) return "just now";
	const minutes = Math.floor(seconds / 60);
	if (minutes < 60) return `${minutes}m ago`;
	const hours = Math.floor(minutes / 60);
	if (hours < 24) return `${hours}h ago`;
	const days = Math.floor(hours / 24);
	return `${days}d ago`;
}

export function LeadsPanel({ leads }: LeadsPanelProps) {
	const [searchQuery, setSearchQuery] = useState("");
	const [filterStatus, setFilterStatus] = useState<string | null>(null);
	const [selectedLeadId, setSelectedLeadId] = useState<string | null>(null);

	const filteredLeads = leads.filter((lead) => {
		const matchesSearch =
			!searchQuery ||
			lead.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
			lead.email.toLowerCase().includes(searchQuery.toLowerCase());
		const matchesFilter =
			!filterStatus || lead.status === filterStatus;
		return matchesSearch && matchesFilter;
	});

	const stats = {
		total: leads.length,
		qualified: leads.filter((l) => l.status === "qualified").length,
		converted: leads.filter((l) => l.status === "converted").length,
		avgScore: Math.round(
			leads.reduce((sum, l) => sum + l.score, 0) / leads.length
		),
	};

	const selectedLead = leads.find((l) => l.id === selectedLeadId);

	return (
		<div className="flex flex-col h-full">
			{/* Header */}
			<div className="px-4 py-3 border-b border-white/[0.06]">
				<div className="flex items-center justify-between mb-3">
					<div className="flex items-center gap-2">
						<div className="w-8 h-8 rounded-lg bg-gradient-to-br from-pink-500 to-rose-600 flex items-center justify-center">
							<Target size={16} className="text-white" />
						</div>
						<div>
							<h2 className="text-sm font-bold text-zinc-200">
								Lead Pipeline
							</h2>
							<p className="text-[10px] text-zinc-500">
								Auto-qualified by AI
							</p>
						</div>
					</div>
				</div>

				{/* Stats row */}
				<div className="grid grid-cols-4 gap-2 mb-3">
					{[
						{
							label: "Total",
							value: stats.total,
							icon: Users,
							color: "text-indigo-400",
							bg: "bg-indigo-500/10",
						},
						{
							label: "Qualified",
							value: stats.qualified,
							icon: Star,
							color: "text-amber-400",
							bg: "bg-amber-500/10",
						},
						{
							label: "Converted",
							value: stats.converted,
							icon: CheckCircle2,
							color: "text-emerald-400",
							bg: "bg-emerald-500/10",
						},
						{
							label: "Avg Score",
							value: stats.avgScore,
							icon: BarChart3,
							color: "text-cyan-400",
							bg: "bg-cyan-500/10",
						},
					].map((stat) => (
						<div
							key={stat.label}
							className={`${stat.bg} rounded-lg px-2 py-1.5 text-center`}
						>
							<stat.icon
								size={12}
								className={`${stat.color} mx-auto mb-0.5`}
							/>
							<p
								className={`text-sm font-bold ${stat.color}`}
							>
								{stat.value}
							</p>
							<p className="text-[8px] text-zinc-500 uppercase tracking-wider">
								{stat.label}
							</p>
						</div>
					))}
				</div>

				{/* Search */}
				<div className="relative">
					<Search
						size={14}
						className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500"
					/>
					<Input
						value={searchQuery}
						onChange={(e) => setSearchQuery(e.target.value)}
						placeholder="Search leads..."
						className="pl-8 h-8 text-xs rounded-full bg-white/[0.04] border-white/[0.08] text-zinc-200 placeholder:text-zinc-500"
					/>
				</div>

				{/* Filter pills */}
				<div className="flex flex-wrap gap-1.5 mt-2.5">
					<button
						onClick={() => setFilterStatus(null)}
						className={`text-[10px] px-2.5 py-1 rounded-full font-medium transition-colors ${
							!filterStatus
								? "bg-white/[0.12] text-zinc-200"
								: "bg-white/[0.04] text-zinc-500 hover:bg-white/[0.08]"
						}`}
					>
						All
					</button>
					{Object.entries(STATUS_CONFIG).map(
						([status, config]) => (
							<button
								key={status}
								onClick={() =>
									setFilterStatus(
										filterStatus === status
											? null
											: status
									)
								}
								className={`text-[10px] px-2.5 py-1 rounded-full font-medium transition-colors flex items-center gap-1 ${
									filterStatus === status
										? "bg-white/[0.12] text-zinc-200"
										: "bg-white/[0.04] text-zinc-500 hover:bg-white/[0.08]"
								}`}
							>
								{config.label}
							</button>
						)
					)}
				</div>
			</div>

			{/* Lead list */}
			<div className="flex-1 overflow-y-auto">
				{selectedLead ? (
					<LeadDetail
						lead={selectedLead}
						onBack={() => setSelectedLeadId(null)}
					/>
				) : (
					<div className="p-2 space-y-1">
						{filteredLeads.map((lead) => {
							const statusConfig =
								STATUS_CONFIG[lead.status];
							const StatusIcon = statusConfig.icon;
							const ScoreIcon = getScoreIcon(lead.score);

							return (
								<button
									key={lead.id}
									onClick={() =>
										setSelectedLeadId(lead.id)
									}
									className="w-full text-left p-3 rounded-xl hover:bg-white/[0.03] transition-colors group border border-transparent hover:border-white/[0.06]"
								>
									<div className="flex items-start gap-3">
										{/* Score indicator */}
										<div className="relative">
											<div
												className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-xs ${getScoreBg(
													lead.score
												)}`}
											>
												{lead.score}
											</div>
											{lead.score >= 80 && (
												<Flame
													size={10}
													className="absolute -top-1 -right-1 text-orange-400"
												/>
											)}
										</div>

										{/* Info */}
										<div className="flex-1 min-w-0">
											<div className="flex items-center justify-between">
												<p className="text-sm font-semibold text-zinc-200 truncate">
													{lead.name}
												</p>
												<ChevronRight
													size={14}
													className="text-zinc-600 shrink-0 group-hover:text-zinc-400 transition-colors"
												/>
											</div>
											<p className="text-[11px] text-zinc-500 truncate">
												{lead.email}
											</p>
											<div className="flex items-center gap-2 mt-1.5">
												<span
													className={`inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full ${statusConfig.bg} ${statusConfig.color} border ${statusConfig.border}`}
												>
													<StatusIcon
														size={9}
													/>
													{statusConfig.label}
												</span>
												<span className="text-[10px] text-zinc-500 flex items-center gap-1">
													<Clock size={9} />
													{timeAgo(
														lead.capturedAt
													)}
												</span>
											</div>
										</div>
									</div>

									{/* Tags */}
									{lead.tags.length > 0 && (
										<div className="flex flex-wrap gap-1 mt-2 ml-[52px]">
											{lead.tags.map((tag) => (
												<span
													key={tag}
													className="text-[9px] px-1.5 py-0.5 rounded-md bg-white/[0.06] text-zinc-400 font-medium"
												>
													{tag}
												</span>
											))}
										</div>
									)}
								</button>
							);
						})}

						{filteredLeads.length === 0 && (
							<div className="text-center py-8">
								<Users
									size={24}
									className="text-zinc-600 mx-auto mb-2"
								/>
								<p className="text-xs text-zinc-500">
									No leads found
								</p>
							</div>
						)}
					</div>
				)}
			</div>
		</div>
	);
}

function LeadDetail({
	lead,
	onBack,
}: {
	lead: Lead;
	onBack: () => void;
}) {
	const statusConfig = STATUS_CONFIG[lead.status];
	const StatusIcon = statusConfig.icon;

	return (
		<div className="p-4 space-y-4">
			<button
				onClick={onBack}
				className="text-[11px] text-indigo-400 font-medium hover:underline flex items-center gap-1"
			>
				<ChevronRight size={12} className="rotate-180" />
				Back to leads
			</button>

			{/* Lead header */}
			<div className="text-center">
				<div
					className={`w-16 h-16 rounded-2xl mx-auto flex items-center justify-center text-white font-bold text-xl ${getScoreBg(
						lead.score
					)}`}
				>
					{lead.score}
				</div>
				<h3 className="text-lg font-bold text-zinc-200 mt-2">
					{lead.name}
				</h3>
				<p className="text-xs text-zinc-500">{lead.email}</p>
				<span
					className={`inline-flex items-center gap-1 text-[11px] font-medium px-3 py-1 rounded-full mt-2 ${statusConfig.bg} ${statusConfig.color} border ${statusConfig.border}`}
				>
					<StatusIcon size={11} />
					{statusConfig.label}
				</span>
			</div>

			<Separator />

			{/* Qualification details */}
			<div>
				<h4 className="text-xs font-bold text-zinc-400 mb-2 flex items-center gap-1.5">
					<Zap size={12} className="text-amber-500" />
					Qualification Score Breakdown
				</h4>
				<div className="space-y-2">
					{[
						{
							label: "Engagement",
							value: Math.min(100, lead.score + 5),
						},
						{
							label: "Intent Signals",
							value: Math.max(0, lead.score - 10),
						},
						{
							label: "Fit Score",
							value: Math.min(100, lead.score + 8),
						},
					].map((metric) => (
						<div key={metric.label}>
							<div className="flex justify-between text-[10px] text-zinc-500 mb-1">
								<span>{metric.label}</span>
								<span className="font-semibold">
									{metric.value}%
								</span>
							</div>
							<div className="h-1.5 bg-white/[0.06] rounded-full overflow-hidden">
								<div
									className={`h-full rounded-full ${getScoreBg(
										metric.value
									)}`}
									style={{
										width: `${metric.value}%`,
									}}
								/>
							</div>
						</div>
					))}
				</div>
			</div>

			<Separator />

			{/* Chat history */}
			{lead.messages.length > 0 && (
				<div>
					<h4 className="text-xs font-bold text-zinc-400 mb-2 flex items-center gap-1.5">
						<MessageSquare size={12} className="text-indigo-400" />
						Chat History
					</h4>
					<div className="space-y-2">
						{lead.messages.map((msg) => (
							<div
								key={msg.id}
								className={`text-[11px] px-3 py-2 rounded-lg ${
									msg.role === "user"
										? "bg-indigo-500/10 text-indigo-200 ml-4"
										: "bg-white/[0.04] text-zinc-300 mr-4"
								}`}
							>
								{msg.content}
							</div>
						))}
					</div>
				</div>
			)}

			{/* Tags */}
			{lead.tags.length > 0 && (
				<div>
					<h4 className="text-xs font-bold text-zinc-400 mb-2">
						Tags
					</h4>
					<div className="flex flex-wrap gap-1.5">
						{lead.tags.map((tag) => (
							<Badge
								key={tag}
								variant="secondary"
								className="text-[10px]"
							>
								{tag}
							</Badge>
						))}
					</div>
				</div>
			)}
		</div>
	);
}

